// src/api/appointments.js
import axios from "axios";
import { API_BASE } from "./base";

const API = API_BASE;
const DEFAULT_APPOINTMENT_LIST_LIMIT = 5000;

function authHeaders() {
  return {
    "Content-Type": "application/json",
  };
}

/**
 * List appointments.
 *
 * Supported filters:
 * - salon_id
 * - staff_id
 * - client_id
 * - status
 * - date (YYYY-MM-DD)
 * - only_future
 * - exclude_cancelled
 * - from_datetime (ISO-8601)
 * - to_datetime (ISO-8601)
 */
export async function listAppointments(params = {}, options = {}) {
  // options: { signal?: AbortSignal, headers?: Record<string, string> }
  const { signal, headers: extraHeaders } = options || {};
  const nextParams = { ...params };
  if (nextParams.limit == null) {
    nextParams.limit = DEFAULT_APPOINTMENT_LIST_LIMIT;
  }
  const res = await axios.get(`${API}/appointments`, {
    params: nextParams,
    headers: {
      ...authHeaders(),
      ...(extraHeaders || {}),
    },
    signal,
  });
  return res.data;
}

export async function getScheduleEstimatedRevenue(params = {}, options = {}) {
  const { signal } = options || {};
  const res = await axios.get(`${API}/appointments/schedule/estimated-revenue`, {
    params,
    headers: authHeaders(),
    signal,
  });
  return res.data;
}

/**
 * Get a single appointment.
 */
export async function getAppointment(appointmentId) {
  const res = await axios.get(`${API}/appointments/${appointmentId}`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function getAppointmentLogs(appointmentId) {
  const res = await axios.get(`${API}/appointments/${appointmentId}/logs`, {
    headers: authHeaders(),
  });
  return res.data;
}

/**
 * Create an appointment.
 *
 * Example:
 * {
 *   salon_id: 1,
 *   client_id: 1,
 *   staff_id: 1,
 *   service_id: 1,
 *   start_time: "2025-12-05T10:00:00",
 *   end_time: null,
 *   total_price: null,
 *   notes: "First visit"
 * }
 */
export async function createAppointment(data, options = {}) {
  const { allowPast = false } = options;
  const body = { ...data, allow_past: allowPast };

  const res = await axios.post(`${API}/appointments`, body, {
    headers: authHeaders(),
  });
  return res.data;
}

/**
 * Update an appointment.
 *
 * Pass allowDoubleBook=true to intentionally permit overlapping bookings.
 */
export async function updateAppointment(id, data, options = {}) {
  const { allowDoubleBook = false, allowPast = false } = options;
  const body = { ...data };
  if (allowDoubleBook) {
    body.allow_double_book = true;
  }
  if (allowPast) {
    body.allow_past = true;
  }

  const res = await axios.put(`${API}/appointments/${id}`, body, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function assignClientToAppointment(appointmentId, salonId, clientId) {
  const res = await axios.patch(
    `${API}/appointments/${appointmentId}/assign-client`,
    {
      salon_id: Number(salonId),
      client_id: Number(clientId),
    },
    {
      headers: authHeaders(),
    }
  );
  return res.data;
}

export async function rescheduleAppointmentGroup(appointmentId, body) {
  const res = await axios.post(
    `${API}/appointments/${appointmentId}/reschedule-group`,
    body,
    {
      headers: authHeaders(),
    }
  );
  return res.data;
}

export async function reschedulePreview(appointmentId, body) {
  const res = await axios.post(
    `${API}/appointments/${appointmentId}/reschedule-preview`,
    body,
    {
      headers: authHeaders(),
    }
  );
  return res.data;
}

export async function validateReschedulePlan(body) {
  const res = await axios.post(`${API}/appointments/reschedule/validate`, body, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function rescheduleAllServices(body) {
  const res = await axios.post(`${API}/appointments/reschedule/all`, body, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function validateRecurringPlan(appointmentId, body) {
  const res = await axios.post(
    `${API}/appointments/${appointmentId}/recurring/validate`,
    body,
    {
      headers: authHeaders(),
    }
  );
  return res.data;
}

export async function createRecurringAppointments(appointmentId, body) {
  const res = await axios.post(
    `${API}/appointments/${appointmentId}/recurring/create`,
    body,
    {
      headers: authHeaders(),
    }
  );
  return res.data;
}

export async function validatePrebookPlan(body) {
  const res = await axios.post(`${API}/appointments/prebook/validate`, body, {
    headers: authHeaders(),
  });
  return res.data;
}

/**
 * Update appointment notes only.
 */
export async function updateAppointmentNotes(appointmentId, notes) {
  const res = await axios.put(
    `${API}/appointments/${appointmentId}/notes`,
    { notes },
    { headers: authHeaders() }
  );
  return res.data;
}

/**
 * Update appointment status.
 *
 * Example:
 * {
 *   status: "confirmed",
 *   notes: "Confirmed by phone"
 * }
 */
export async function updateAppointmentStatus(appointmentId, data) {
  const res = await axios.put(
    `${API}/appointments/${appointmentId}/status`,
    data,
    {
      headers: authHeaders(),
    }
  );
  return res.data;
}

export async function checkInAppointment(appointmentId) {
  const res = await axios.post(
    `${API}/appointments/${appointmentId}/check-in`,
    null,
    { headers: authHeaders() }
  );
  return res.data;
}

export async function undoCheckInAppointment(appointmentId) {
  const res = await axios.post(
    `${API}/appointments/${appointmentId}/undo-check-in`,
    null,
    { headers: authHeaders() }
  );
  return res.data;
}

export async function confirmVisit(appointmentId) {
  const res = await axios.post(
    `${API}/appointments/${appointmentId}/confirm-visit`,
    null,
    { headers: authHeaders() }
  );
  return res.data;
}

export async function unconfirmVisit(appointmentId) {
  const res = await axios.post(
    `${API}/appointments/${appointmentId}/unconfirm-visit`,
    null,
    { headers: authHeaders() }
  );
  return res.data;
}

export async function confirmAppointment(appointmentId) {
  const res = await axios.post(
    `${API}/appointments/${appointmentId}/confirm`,
    null,
    { headers: authHeaders() }
  );
  return res.data;
}

export async function toggleAppointmentConfirm(appointmentId) {
  const res = await axios.post(
    `${API}/appointments/${appointmentId}/toggle-confirm`,
    null,
    { headers: authHeaders() }
  );
  return res.data;
}

export async function sendAppointmentConfirmationEmail(
  appointmentId,
  body = {}
) {
  const res = await axios.post(
    `${API}/appointments/${appointmentId}/send-confirmation-email`,
    body,
    { headers: authHeaders() }
  );
  return res.data;
}

export async function sendFutureAppointmentsEmail(body = {}) {
  const res = await axios.post(
    `${API}/appointments/send-future-list-email`,
    body,
    { headers: authHeaders() }
  );
  return res.data;
}

export async function cancelAppointment(appointmentId, body = null, options = {}) {
  const { cancelFutureRecurrence = false } = options || {};
  const payload = {
    ...(body || {}),
    ...(cancelFutureRecurrence ? { cancel_future_recurrence: true } : {}),
  };
  const res = await axios.post(
    `${API}/appointments/${appointmentId}/cancel`,
    payload,
    { headers: authHeaders() }
  );
  return res.data;
}

export async function deleteAppointment(appointmentId, options = {}) {
  const { deleteFutureRecurrence = false } = options || {};
  const res = await axios.delete(`${API}/appointments/${appointmentId}`, {
    headers: authHeaders(),
    params: deleteFutureRecurrence
      ? { delete_future_recurrence: true }
      : undefined,
  });
  return res.data;
}

export async function getAppointmentAttachmentMeta(appointmentId) {
  const res = await axios.get(`${API}/appointments/${appointmentId}/attachment`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function getAppointmentAttachmentFile(appointmentId) {
  const res = await axios.get(
    `${API}/appointments/${appointmentId}/attachment/file`,
    {
      headers: authHeaders(),
      responseType: "blob",
    }
  );
  return res.data;
}

export async function saveAttachmentToDrive(appointmentId, attachmentId) {
  const res = await axios.post(
    `${API}/appointments/${appointmentId}/attachments/${attachmentId}/save-to-drive`,
    {},
    {
      headers: authHeaders(),
    }
  );
  return res.data;
}
